"use client"

import { useEffect, useState } from "react"

const POLL_INTERVAL_MS = 60_000

/**
 * Polls `/api/chat/availability` (Glue-proxied) for live Chatwoot
 * agent presence. Returns `null` on first render (before the first
 * fetch lands) so callers can avoid flashing "offline" copy; afterwards
 * it returns the last-known boolean.
 *
 * Semantics (see docs/chat-briefings/2026-04-17-agents-online-availability.md):
 *   - `online === true`  → at least one non-bot agent is live in
 *     Chatwoot.
 *   - `online === false` → no agents, OR Glue/Chatwoot upstream
 *     failure. Either way callers should degrade gracefully — never
 *     render an error state.
 *
 * Deliberately separate from `useBusinessHours()`: the handoff form
 * still keys off business hours so a single agent flipping offline
 * during lunch doesn't push users into the out-of-hours flow.
 */
function readOverride(): boolean | null {
  if (typeof window === "undefined") return null
  try {
    const raw = sessionStorage.getItem("ahead-chat-force-agents")
    if (raw === "online") return true
    if (raw === "offline") return false
    return null
  } catch {
    return null
  }
}

export function useAgentsOnline(): boolean | null {
  const [online, setOnline] = useState<boolean | null>(null)

  useEffect(() => {
    // `ahead-chat-force-agents` sessionStorage key short-circuits the
    // real poll for QA — used by the dev chat page / Playwright suite.
    const override = readOverride()
    if (override !== null) {
      setOnline(override)
      return
    }

    const controller = new AbortController()

    async function poll(): Promise<void> {
      try {
        const res = await fetch("/api/chat/availability", {
          signal: controller.signal,
        })
        if (!res.ok) return
        const data = (await res.json()) as { online?: boolean }
        if (typeof data.online === "boolean") setOnline(data.online)
      } catch {
        // Network / abort — keep the last value, don't flip to offline
        // just because a single poll was interrupted.
      }
    }

    void poll()
    const id = window.setInterval(() => void poll(), POLL_INTERVAL_MS)
    return () => {
      controller.abort()
      window.clearInterval(id)
    }
  }, [])

  return online
}
